import React, { useRef, useState } from "react";
import { Upload, Sparkles, Loader2, RotateCcw, ImageIcon, X, Columns } from "lucide-react";
import { generateRender, renderImageUrl } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const ANGLES = [
  { id: "exterior", label: "Exterior" },
  { id: "aerial", label: "Aerial" },
  { id: "side", label: "Side" },
  { id: "interior", label: "Interior" },
  { id: "dusk", label: "Dusk" },
];

const RenderStudio = () => {
  const { user, login, checkAuth } = useAuth();
  const { toast } = useToast();
  const [prompt, setPrompt] = useState(
    "Modern two-story single-family home, white siding with black trim, large windows, landscaped front yard"
  );
  const [fileB64, setFileB64] = useState(null);
  const [fileName, setFileName] = useState("");
  const [renders, setRenders] = useState({}); // { angle: id }
  const [active, setActive] = useState(null);
  const [busyAngle, setBusyAngle] = useState(null);
  const [error, setError] = useState(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);
  const inputRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setFileB64(reader.result);
    reader.readAsDataURL(file);
  };

  const run = async (angle) => {
    if (!prompt.trim() || busyAngle) return;
    if (!user) {
      toast({ title: "Sign in to render", description: "Free accounts get 3 render points. Redirecting…" });
      setTimeout(login, 800);
      return;
    }
    setBusyAngle(angle);
    setError(null);
    try {
      const data = await generateRender({ prompt, angle, image_base64: fileB64 });
      setRenders((r) => ({ ...r, [angle]: data.id }));
      setActive(angle);
      checkAuth(); // refresh points balance
    } catch (e) {
      if (e?.response?.status === 402) {
        setError("You're out of render points. Upgrade your plan or buy a points pack below.");
        toast({ title: "Out of render points", description: "Grab more from the Pricing section." });
      } else if (e?.response?.status === 401) {
        setTimeout(login, 400);
      } else {
        setError("Couldn't generate the render. Please try again.");
      }
    } finally {
      setBusyAngle(null);
    }
  };

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: py * -10, y: px * 12 });
  };

  const activeId = active ? renders[active] : null;

  const angleKeys = Object.keys(renders);
  const toggleCompare = () => {
    const next = !compareMode;
    setCompareMode(next);
    if (next && angleKeys.length >= 2) {
      setCompareA(angleKeys[0]);
      setCompareB(angleKeys[1]);
    }
  };
  const labelFor = (id) => ANGLES.find((a) => a.id === id)?.label || id;

  return (
    <section id="studio" className="py-24 bg-[#0A2540] bp-grid">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-sm font-semibold uppercase tracking-wider text-[#f87171]">New · AI Vision</span>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl font-700 tracking-tight text-white">
            Blueprint → 3D render studio
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Upload a floor plan or just describe your build — AI renders a photorealistic view you can orbit.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          {/* Controls */}
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <label className="text-sm font-medium text-slate-200">Describe the building &amp; style</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              className="mt-2 w-full rounded-lg bg-white/10 border border-white/15 text-white placeholder:text-slate-400 px-3.5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/50"
              placeholder="e.g. Craftsman bungalow, cedar shingles, wraparound porch…"
            />

            <div className="mt-4">
              <label className="text-sm font-medium text-slate-200">Floor plan / blueprint (optional)</label>
              <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
              {!fileB64 ? (
                <button
                  onClick={() => inputRef.current?.click()}
                  className="mt-2 w-full flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-white/20 py-7 text-slate-300 hover:border-[#1D4ED8] hover:text-white transition-colors"
                >
                  <Upload className="h-6 w-6" />
                  <span className="text-sm">Click to upload a floor plan image</span>
                </button>
              ) : (
                <div className="mt-2 flex items-center gap-3 rounded-lg border border-white/15 bg-white/5 p-3">
                  <img src={fileB64} alt="plan" className="h-14 w-14 rounded object-cover" />
                  <span className="flex-1 text-sm text-slate-200 truncate">{fileName}</span>
                  <button onClick={() => { setFileB64(null); setFileName(""); }} className="text-slate-400 hover:text-[#f87171]">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="mt-5">
              <div className="text-sm font-medium text-slate-200 mb-2">Camera angle</div>
              <div className="flex flex-wrap gap-2">
                {ANGLES.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => run(a.id)}
                    disabled={!!busyAngle}
                    className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm transition-colors disabled:opacity-60 ${
                      renders[a.id]
                        ? "border-[#1D4ED8] bg-[#1D4ED8] text-white"
                        : "border-white/20 text-slate-200 hover:border-[#1D4ED8]"
                    }`}
                  >
                    {busyAngle === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    {a.label}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-400">Generate multiple angles, then switch between them to orbit your build.</p>
            </div>
            {error && <p className="mt-3 text-sm text-red-300">{error}</p>}
          </div>

          {/* Viewer */}
          <div className="rounded-2xl bg-black/30 border border-white/10 p-6 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-200">
                {compareMode ? "Compare angles" : "Viewer"}
              </span>
              <button
                onClick={toggleCompare}
                disabled={angleKeys.length < 2}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 ${
                  compareMode ? "bg-[#DC2626] text-white" : "border border-white/20 text-slate-200 hover:border-[#1D4ED8]"
                }`}
                title={angleKeys.length < 2 ? "Generate 2+ angles to compare" : ""}
              >
                <Columns className="h-3.5 w-3.5" /> Compare
              </button>
            </div>

            {compareMode ? (
              <div className="flex-1 grid grid-cols-2 gap-3">
                {[[compareA, setCompareA], [compareB, setCompareB]].map(([val, setter], idx) => (
                  <div key={idx} className="flex flex-col">
                    <Select value={val || ""} onValueChange={setter}>
                      <SelectTrigger className="mb-2 bg-white/10 border-white/15 text-white h-9 text-sm">
                        <SelectValue placeholder="Pick angle" />
                      </SelectTrigger>
                      <SelectContent>
                        {angleKeys.map((a) => (
                          <SelectItem key={a} value={a}>{labelFor(a)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="relative flex-1 min-h-[260px] rounded-xl overflow-hidden bg-slate-900">
                      {val && renders[val] ? (
                        <img src={renderImageUrl(renders[val])} alt={val} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full flex items-center justify-center text-slate-500 text-sm">Pick an angle</div>
                      )}
                      <span className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2.5 py-1 text-xs text-white">{val ? labelFor(val) : "—"}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <div
              className="relative flex-1 min-h-[320px] rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center"
              style={{ perspective: "1200px" }}
              onMouseMove={onMove}
              onMouseLeave={() => setTilt({ x: 0, y: 0 })}
            >
              {busyAngle && !activeId ? (
                <div className="flex flex-col items-center gap-3 text-slate-300">
                  <Loader2 className="h-8 w-8 animate-spin text-[#1D4ED8]" />
                  <span className="text-sm">Rendering your build… (10-25s)</span>
                </div>
              ) : activeId ? (
                <img
                  src={renderImageUrl(activeId)}
                  alt="render"
                  onClick={() => setZoom((z) => !z)}
                  className="h-full w-full object-cover cursor-zoom-in transition-transform duration-200"
                  style={{
                    transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${zoom ? 1.4 : 1})`,
                  }}
                />
              ) : (
                <div className="flex flex-col items-center gap-3 text-slate-500">
                  <ImageIcon className="h-10 w-10" />
                  <span className="text-sm">Pick a camera angle to generate your first render</span>
                </div>
              )}
              {activeId && (
                <div className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-black/50 px-3 py-1.5 text-xs text-white">
                  <RotateCcw className="h-3 w-3" /> Move to orbit · click to zoom
                </div>
              )}
            </div>
            )}

            {/* Thumbnails */}
            {Object.keys(renders).length > 0 && (
              <div className="mt-4 flex gap-3 overflow-x-auto">
                {Object.entries(renders).map(([angle, id]) => (
                  <button
                    key={angle}
                    onClick={() => setActive(angle)}
                    className={`shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${
                      active === angle ? "border-[#DC2626]" : "border-transparent hover:border-white/40"
                    }`}
                  >
                    <img src={renderImageUrl(id)} alt={angle} className="h-16 w-24 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RenderStudio;
